import React, { useEffect, useRef, useState } from 'react';
import useIncome from '../hooks/useIncome';
import incomeService from '../services/incomeService';

const EditIncomeWizard = ({ income, onCancel, onSubmitManual, onSubmitRaise, onSubmitJobChange }) => {
  const [step, setStep] = useState('chooser');
  // Hooks must be declared unconditionally
  const [raiseForm, setRaiseForm] = useState({ effectiveDate: '', newBase: '', bonusNow: '', bonusTargetPercent: '' });
  const [jobForm, setJobForm] = useState({ startDate: '', employer: '', grossAnnual: '', basePerPeriod: '', frequency: '', signingBonus: '', bonusTargetPercent: '', bonusTargetFixed: '', rsu: '', rsuGrantUsd: '', vestCliffMonths: '12', vestMonths: '48', estSharePrice: '', location: { state: '', city: '', zipCode: '', country: 'USA' }, jobSite: '', replaceIncomeId: income?._id });
  const [netPreview, setNetPreview] = useState(null);

  const toPerPeriod = (annual, freq) => {
    const a = Number(annual || 0);
    const f = String(freq || '').toLowerCase();
    if (!a || !f) return '';
    const val = f === 'weekly' ? a / 52
      : (f === 'biweekly' ? a / 26
      : (f === 'semi-monthly' ? a / 24
      : (f === 'monthly' ? a / 12
      : (f === 'yearly' ? a : a / 12))));
    return (Math.round(val * 100) / 100).toString();
  };
  const [allIncomes, setAllIncomes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [analysis, setAnalysis] = useState('');
  const fileInputRef = useRef(null);

  // Load incomes to allow choosing which one to replace
  const { fetchIncomes } = useIncome();
  useEffect(() => {
    (async () => {
      try {
        const response = await incomeService.getAll();
        const list = response?.data || [];
        setAllIncomes(list);
      } catch (e) {
        try {
          const fetched = await fetchIncomes();
          if (Array.isArray(fetched?.data)) setAllIncomes(fetched.data);
        } catch (_) {}
      }
    })();
  }, [fetchIncomes]);

  if (step === 'chooser') {
    return (
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">What changed?</h3>
        <div className="space-y-2">
          <button className="w-full bg-indigo-600 text-white rounded-md py-2" onClick={() => setStep('raise')}>I got a raise/bonus</button>
          <button className="w-full bg-emerald-600 text-white rounded-md py-2" onClick={() => setStep('job')}>I'm switching jobs</button>
          <button className="w-full bg-gray-100 text-gray-900 rounded-md py-2 border" onClick={onSubmitManual}>Edit manually</button>
        </div>
        <button className="mt-4 text-sm text-gray-600" onClick={onCancel}>Cancel</button>
      </div>
    );
  }

  if (step === 'raise') {
    return (
      <div className="p-4 space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">Raise/bonus</h3>
        <input className="w-full border rounded p-2" placeholder="Effective date" type="date" value={raiseForm.effectiveDate} onChange={e => setRaiseForm({ ...raiseForm, effectiveDate: e.target.value })} />
        <input className="w-full border rounded p-2" placeholder="New base per pay period" value={raiseForm.newBase} onChange={e => setRaiseForm({ ...raiseForm, newBase: e.target.value })} />
        <input className="w-full border rounded p-2" placeholder="One-time bonus (optional)" value={raiseForm.bonusNow} onChange={e => setRaiseForm({ ...raiseForm, bonusNow: e.target.value })} />
        <input className="w-full border rounded p-2" placeholder="Annual bonus target % (optional)" value={raiseForm.bonusTargetPercent} onChange={e => setRaiseForm({ ...raiseForm, bonusTargetPercent: e.target.value })} />
        <div className="pt-2">
          <button className="bg-indigo-600 text-white rounded-md py-2 px-4" onClick={() => onSubmitRaise(raiseForm)}>Save</button>
          <button className="ml-3 text-sm text-gray-600" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    );
  }

  if (step === 'job') {
    return (
      <div className="p-4 space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">Switching jobs</h3>
        {/* Offer-letter analysis */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            className="text-sm bg-gradient-to-r from-fuchsia-500 to-indigo-600 text-white px-3 py-1.5 rounded-full shadow hover:from-fuchsia-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? 'Analyzing…' : 'Analyze offer letter with Wally'}
          </button>
          <input ref={fileInputRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            try {
              setUploading(true);
              const fd = new FormData();
              fd.append('document', file);
              // Extend timeout for this single request
              const res = await incomeService.analyzeOfferLetter(fd);
              const s = res?.data?.suggestions || {};
              setJobForm(prev => ({
                ...prev,
                startDate: s.startDate || prev.startDate,
                employer: s.employer || prev.employer,
                basePerPeriod: s.basePerPeriod || prev.basePerPeriod,
                frequency: s.frequency || prev.frequency,
                signingBonus: s.signingBonus || prev.signingBonus,
                bonusTargetPercent: s.bonusTargetPercent || prev.bonusTargetPercent,
                bonusTargetFixed: s.bonusTargetFixed || prev.bonusTargetFixed,
                rsu: s.rsu || prev.rsu,
                rsuGrantUsd: s.rsuGrantUsd || prev.rsuGrantUsd,
                vestCliffMonths: s.vestCliffMonths || prev.vestCliffMonths,
                vestMonths: s.vestMonths || prev.vestMonths,
                estSharePrice: s.estSharePrice || prev.estSharePrice
              }));
              if (res?.data?.analysis) setAnalysis(res.data.analysis);
            } finally {
              setUploading(false);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }
          }} />
        </div>

        {/* Which income to replace */}
        <select
          className="w-full border rounded p-2"
          value={jobForm.replaceIncomeId || income?._id}
          onChange={e => setJobForm({ ...jobForm, replaceIncomeId: e.target.value })}
        >
          {(allIncomes || []).map(inc => {
            const employerName = inc?.typeSpecificDetails?.salary?.employer?.name || 'Employer';
            const freq = inc.frequency || inc.typeSpecificDetails?.salary?.payDetails?.frequency || 'monthly';
            return (
              <option key={inc._id} value={inc._id}>
                Replace: {inc.name} - {employerName} ({freq})
              </option>
            );
          })}
        </select>
        <input className="w-full border rounded p-2" placeholder="Start date" type="date" value={jobForm.startDate} onChange={e => setJobForm({ ...jobForm, startDate: e.target.value })} />
        <input className="w-full border rounded p-2" placeholder="Employer name" value={jobForm.employer} onChange={e => setJobForm({ ...jobForm, employer: e.target.value })} />
        <div className="grid grid-cols-2 gap-2">
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-gray-500">$</span>
            <input
              className="border rounded p-2 pl-6 w-full"
              placeholder="Gross annual pay (required)"
              value={jobForm.grossAnnual}
              onChange={e => {
                const grossAnnual = e.target.value;
                setJobForm(prev => ({
                  ...prev,
                  grossAnnual,
                  basePerPeriod: toPerPeriod(grossAnnual, prev.frequency)
                }));
              }}
            />
          </div>
          <select
            className="border rounded p-2"
            value={jobForm.frequency}
            onChange={e => {
              const frequency = e.target.value;
              setJobForm(prev => ({
                ...prev,
                frequency,
                basePerPeriod: toPerPeriod(prev.grossAnnual, frequency)
              }));
            }}
          >
            <option value="">Select Pay Period</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Bi-Weekly</option>
            <option value="semi-monthly">Semi-Monthly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
        <div className="relative">
          <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-gray-500">$</span>
          <input
            className="border rounded p-2 pl-6 w-full"
            placeholder="Base per pay period (auto)"
            value={jobForm.basePerPeriod}
            onChange={e => setJobForm({ ...jobForm, basePerPeriod: e.target.value })}
          />
        </div>
        {/* Location and job site */}
        <div className="grid grid-cols-4 gap-2">
          <input className="border rounded p-2" placeholder="ZIP" value={jobForm.location.zipCode} onChange={async e => {
            const zip = e.target.value.replace(/[^0-9]/g, '').slice(0,5);
            setJobForm(prev => ({ ...prev, location: { ...prev.location, zipCode: zip } }));
            if (zip.length === 5) {
              try {
                const resp = await incomeService.lookupZip(zip);
                if (resp?.success) {
                  setJobForm(prev => ({
                    ...prev,
                    location: {
                      ...prev.location,
                      city: resp.city || prev.location.city,
                      state: (resp.stateCode || resp.state || prev.location.state) || '',
                      country: 'USA'
                    }
                  }));
                }
              } catch (_) {}
            }
          }} />
          <input className="border rounded p-2" placeholder="City" value={jobForm.location.city} onChange={e => setJobForm({ ...jobForm, location: { ...jobForm.location, city: e.target.value } })} />
          <select className="border rounded p-2" value={jobForm.location.state} onChange={e => setJobForm({ ...jobForm, location: { ...jobForm.location, state: e.target.value } })}>
            <option value="">State (required)</option>
            {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select className="border rounded p-2" value={jobForm.jobSite} onChange={e => setJobForm({ ...jobForm, jobSite: e.target.value })}>
            <option value="">Select Job Site</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
            <option value="on_site">On-site</option>
          </select>
        </div>
        <div className="relative">
          <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-gray-500">$</span>
          <input className="w-full border rounded p-2 pl-6" placeholder="Signing bonus (optional)" value={jobForm.signingBonus} onChange={e => setJobForm({ ...jobForm, signingBonus: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="relative">
            <input className="border rounded p-2 pr-6 w-full" placeholder="Annual bonus target %" value={jobForm.bonusTargetPercent} onChange={e => setJobForm({ ...jobForm, bonusTargetPercent: e.target.value })} />
            <span className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-500">%</span>
          </div>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-gray-500">$</span>
            <input className="border rounded p-2 pl-6 w-full" placeholder="Annual bonus target $" value={jobForm.bonusTargetFixed} onChange={e => setJobForm({ ...jobForm, bonusTargetFixed: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <input className="border rounded p-2" placeholder="Total RSU shares" value={jobForm.rsu} onChange={e => setJobForm({ ...jobForm, rsu: e.target.value })} />
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-gray-500">$</span>
            <input className="border rounded p-2 pl-6 w-full" placeholder="RSU grant value ($)" value={jobForm.rsuGrantUsd} onChange={e => setJobForm({ ...jobForm, rsuGrantUsd: e.target.value })} />
          </div>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-gray-500">$</span>
            <input className="border rounded p-2 pl-6 w-full" placeholder="Est. share price ($)" value={jobForm.estSharePrice} onChange={e => setJobForm({ ...jobForm, estSharePrice: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input className="border rounded p-2" placeholder="Cliff months (e.g., 12)" value={jobForm.vestCliffMonths} onChange={e => setJobForm({ ...jobForm, vestCliffMonths: e.target.value })} />
          <input className="border rounded p-2" placeholder="Total vest months (e.g., 48)" value={jobForm.vestMonths} onChange={e => setJobForm({ ...jobForm, vestMonths: e.target.value })} />
        </div>
        {/* Live net preview */}
        <div className="flex items-center justify-between text-sm text-gray-700">
          <button
            className="text-indigo-600 hover:text-indigo-700"
            onClick={async () => {
              try {
                const freq = (jobForm.frequency || 'monthly').toLowerCase();
                const res = await incomeService.estimateNet({
                  grossAnnual: jobForm.grossAnnual ? Number(jobForm.grossAnnual) : undefined,
                  basePerPeriod: jobForm.basePerPeriod ? Number(jobForm.basePerPeriod) : undefined,
                  frequency: freq,
                  taxProfile: { state: jobForm.location.state || undefined }
                });
                setNetPreview(res?.netPerPeriod || null);
              } catch (_) {}
            }}
          >Preview net pay</button>
          {netPreview !== null && (
            <div>Estimated net per period: <span className="font-semibold">${(Number(netPreview)).toFixed(2)}</span></div>
          )}
        </div>

        <div className="pt-2">
          <button
            className={`rounded-md py-2 px-4 ${jobForm.grossAnnual && jobForm.frequency && jobForm.location.state ? 'bg-emerald-600 text-white' : 'bg-gray-300 text-gray-600 cursor-not-allowed'}`}
            disabled={!(jobForm.grossAnnual && jobForm.frequency && jobForm.location.state)}
            onClick={() => onSubmitJobChange(jobForm)}
          >
            Save
          </button>
          <button className="ml-3 text-sm text-gray-600" onClick={onCancel}>Cancel</button>
        </div>

        {analysis && (
          <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded text-sm text-indigo-900 whitespace-pre-wrap">
            {analysis}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default EditIncomeWizard;


